/**
 * useOnlineStatus Hook
 * Detects network connectivity status and provides offline handling
 * Uses navigator.onLine and online/offline events
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Online status information
 */
interface OnlineStatus {
  /** Whether the browser is online */
  isOnline: boolean;
  /** Whether we're transitioning from offline to online */
  wasOffline: boolean;
  /** Timestamp of last status change */
  lastStatusChange: Date | null;
  /** Duration offline in milliseconds (if applicable) */
  offlineDuration: number | null;
}

/**
 * Hook to detect and respond to network connectivity changes
 * Provides offline status and recovery information
 *
 * @returns Online status object with connectivity information
 *
 * @example
 * const { isOnline, wasOffline } = useOnlineStatus();
 *
 * if (!isOnline) {
 *   return <OfflineMessage />;
 * }
 */
export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    // SSR-safe initial value
    if (typeof window === 'undefined') return true;
    return navigator.onLine;
  });

  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [lastStatusChange, setLastStatusChange] = useState<Date | null>(null);
  const [offlineStart, setOfflineStart] = useState<Date | null>(null);
  const [offlineDuration, setOfflineDuration] = useState<number | null>(null);

  const handleOnline = useCallback((): void => {
    const now = new Date();
    setIsOnline(true);
    setLastStatusChange(now);
    setWasOffline(true);

    // Calculate offline duration
    if (offlineStart) {
      setOfflineDuration(now.getTime() - offlineStart.getTime());
    }
    setOfflineStart(null);

    // Reset wasOffline flag after a short delay
    // This allows components to show "back online" messages
    setTimeout(() => {
      setWasOffline(false);
    }, 5000);
  }, [offlineStart]);

  const handleOffline = useCallback((): void => {
    const now = new Date();
    setIsOnline(false);
    setLastStatusChange(now);
    setOfflineStart(now);
    setOfflineDuration(null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set initial state
    setIsOnline(navigator.onLine);

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return {
    isOnline,
    wasOffline,
    lastStatusChange,
    offlineDuration,
  };
}

export default useOnlineStatus;
